/** @jsxImportSource @emotion/react */
import { useCallback, useMemo, useRef, useState } from "react";
import { css } from "@emotion/react";
import { useDispatch, useSelector } from "react-redux";
import { Row } from "../../../layout/flexbox";
import * as Buttons from "../../../forms/elements/buttons";
import ErrorView from "../../../forms/ErrorView";
import ConfirmationModal from "../../../forms/elements/ConfirmationModal";
import { IAppState } from "../../../core/redux/IAppState";
import { showSnackbarAction } from "../../../snackbar/snackbarAction";
import { useAccessRulesEditor } from "../useAccessRulesEditor";
import { getAllValidationErrors } from "../validation/validationUtils";
import { hasAnyDateRangeOverlap } from "../blackoutDateRange/blackoutDateRange";
import { getResource } from "../../../language/resources/getResource";
import { LiftAccessRuleSetEditorLayout } from "./LiftAccessRuleSetEditorLayout";
import saveIcon from "../../../assets/icons/disk.svg";
import { BoxedErrorInverse } from "../../../forms/elements/boxedMessage/boxedMessageStyles";
import { ROW_SPACING } from "../../../forms/styles";
import {
  LiftAccessRuleSetEditableHeader,
  LiftAccessRuleSetEditableHeaderHandle,
} from "./LiftAccessRuleSetEditableHeader";
import { LiftAccessRulesEditor } from "./LiftAccessRulesEditor";
import {
  AccessNode,
  AccessOverviewQuery,
  FulfillmentSeason,
  SellingSeason,
} from "../../models";
import {
  compareByNameAsc,
  createEmptyAccessOverviewQuery,
  extractId,
  extractIds,
  RESORT_EXCLUSIONS,
  resortToAccessNode,
  shouldIncludeResort,
} from "../../module";
import { EntitlementType } from "../../../entitlementTypes/entitlementType";
import { Resort } from "../../../resortDetails/resort";
import {
  mapAccessRuleMultiAddRequest,
} from "../requests/accessRuleMultiAddRequestMapper";
import {
  createEmptyChangeSet,
  LIFT_ACCESS_RULE_SCOPE_CATEGORY,
  mapNodeTypeToScopeCategory,
} from "../requests/accessRuleRequestMapper";
import {
  bulkAddLiftAccessRulesBatched,
  flattenAddContexts,
} from "../../bulkAddLiftAccessRulesBatched";
import { BulkProgressCard } from "../../../forms/elements/BulkProgressCard";
import { ACTION_TYPE } from "../../../core/redux/actionType";
import { AccessRuleEntitlementContext } from "../models";
import { AccessRuleMultiAddRequest } from "../requests/accessRuleRequestBuilder";
import { LiftAccessRuleBatchSaveResult } from "../../models";
import {
  BulkFailureIdentity,
  BulkSaveFailuresView,
} from "./BulkSaveFailuresView";
import {
  addFailureCount,
  addSuccessCount,
  BulkAddRun,
  groupContextsForAdd,
  notAttemptedAddContexts,
  retryableAddContexts,
  toAddFailures,
} from "./bulkAddRun";
import { mergeSaveOutcomes } from "./bulkSaveRun";
import { entitlementContextKey } from "./models";

type Props = {
  sellingSeasons: SellingSeason[];
  fulfillmentSeasons: FulfillmentSeason[];
  entitlementTypes: EntitlementType[];
  resorts: Resort[];
  onDismiss: () => void;
  onCreate?: (query: AccessOverviewQuery) => void;
};

export function LiftAccessRuleSetCreator({
  sellingSeasons,
  fulfillmentSeasons,
  entitlementTypes,
  resorts,
  onDismiss,
  onCreate,
}: Props) {
  const dispatch = useDispatch<any>();

  const editableHeaderRef = useRef<LiftAccessRuleSetEditableHeaderHandle>(null);

  const [selectedSellingSeason, setSelectedSellingSeason] = useState<
    SellingSeason | undefined
  >();

  const accessNodes = useMemo<AccessNode[]>(() => {
    if (!resorts) return [];

    return resorts
      .filter((r) =>
        shouldIncludeResort(r, RESORT_EXCLUSIONS.ACCESS_RULE_CREATOR),
      )
      .map(resortToAccessNode)
      .sort(compareByNameAsc);
  }, [resorts]);

  const resources = useSelector((s: IAppState) => s.locale);
  const dateFormat = useSelector(
    (s: IAppState) => s.resortToolsConfig.defaultDateFormat,
  );
  const userId = useSelector((s: IAppState) => s.userState.userId);
  const operatorId = useSelector((s: IAppState) => s.userState.userName);

  const cancelRef = useRef({ cancelled: false });
  const [isCancelling, setIsCancelling] = useState(false);
  const saveProgress = useSelector((s: IAppState) => s.bulkOperationProgress);
  const isBusy = !!saveProgress && saveProgress.total > 0;
  const [isProgressVisible, setIsProgressVisible] = useState(false);

  /** The run and its outcomes, which outlive the requests so Retry can re-send. */
  const [addRun, setAddRun] = useState<BulkAddRun | null>(null);
  const [showAddFailures, setShowAddFailures] = useState(false);
  // The first attempt's request, reused for a retry so the rules and the run's
  // RequestUtcDateTime stay identical — only the contexts narrow.
  const addRequestRef = useRef<AccessRuleMultiAddRequest | null>(null);

  const showSnackbar = useCallback(
    (msg: string) => dispatch(showSnackbarAction(msg)),
    [dispatch],
  );

  const [initialRules] = useState({
    effectiveDateRangeRules: [],
    maxDaysRules: [],
    blackoutDateRangeRules: [],
    blackoutDayOfWeekRules: [],
  });

  const {
    editableRules,
    setEditableRules,
    changeSet,
    hasEmptyNonDeletedBlackoutDayOfWeekGroup,
    resetChanges,
  } = useAccessRulesEditor(initialRules);

  const [submitCount, setSubmitCount] = useState(0);
  const [hasChangesSinceSubmit, setHasChangesSinceSubmit] = useState(false);
  const [validationError, setValidationError] = useState<string>();
  const [showConfirmation, setShowConfirmation] = useState(false);

  if (!editableRules) return null;

  const onAnyEdit = () => {
    setHasChangesSinceSubmit(true);
    setValidationError(undefined);
  };

  const validateAndConfirm = () => {
    const isHeaderValid = editableHeaderRef.current?.validate();

    if (!isHeaderValid) {
      setValidationError(
        getResource(
          resources,
          "ACCESS_RULE_ADD_HEADER_VALIDATION_ERROR_UNABLE_TO_SAVE",
        ),
      );
      return;
    }

    setSubmitCount((c) => c + 1);
    setHasChangesSinceSubmit(false);

    if (
      hasEmptyNonDeletedBlackoutDayOfWeekGroup ||
      hasAnyDateRangeOverlap(editableRules.blackoutDateRangeRules)
    ) {
      setValidationError(
        getResource(
          resources,
          "ACCESS_RULE_EDIT_VALIDATION_ERROR_UNABLE_TO_SAVE",
        ),
      );
      return;
    }

    const validationErrors = getAllValidationErrors([
      changeSet?.effectiveDateRange.created ?? [],
      changeSet?.maxDays.created ?? [],
      changeSet?.blackoutDateRange.created ?? [],
      changeSet?.blackoutDayOfWeek.created ?? [],
    ]);

    if (validationErrors.length > 0) {
      setValidationError(
        getResource(
          resources,
          "ACCESS_RULE_EDIT_VALIDATION_ERROR_UNABLE_TO_SAVE",
        ),
      );
      return;
    }

    setValidationError(undefined);
    setShowConfirmation(true);
  };

  const buildRequest = () => {
    const headerValue = editableHeaderRef.current?.getValue();

    // These conditions should not happen
    if (!headerValue || !headerValue?.fulfillmentSeason || !changeSet) {
      setValidationError(
        getResource(resources, "ACCESS_RULE_ADD_VALIDATION_UNEXPECTED_ERROR"),
      );
      return undefined;
    }

    const requestParams = {
      userId,
      operatorId,
      entitlementTypeIds: extractIds(headerValue!.entitlementTypeIds),
      fulfillmentSeasonId: headerValue!.fulfillmentSeason!.fulfillmentSeasonId,
      sellingSeasonId: headerValue!.sellingSeason!.sellingSeasonId,
      accessNodes: headerValue!.accessNodes,
      changeSet: changeSet ?? createEmptyChangeSet(),
    };

    return mapAccessRuleMultiAddRequest(requestParams);
  };

  /**
   * Display columns per rule set, captured before the run starts: the outcome screen
   * outlives the header, which Close resets.
   */
  const buildAddIdentities = (
    contexts: AccessRuleEntitlementContext[],
  ): Map<string, BulkFailureIdentity> => {
    const headerValue = editableHeaderRef.current?.getValue();

    const entitlementDescriptions = new Map(
      (entitlementTypes ?? []).map((et) => [
        et.entitlementTypeId,
        et.publicDescription,
      ]),
    );

    // Keyed on scope category too: a resort and a location can share an id.
    const locationNames = new Map(
      (headerValue?.accessNodes ?? []).map((node) => [
        `${mapNodeTypeToScopeCategory(node.type)}:${extractId(node.id)}`,
        node.name,
      ]),
    );

    return new Map(
      contexts.map((context) => [
        entitlementContextKey(context),
        {
          entitlementTypeId: context.EntitlementTypeId,
          entitlementDescription:
            entitlementDescriptions.get(context.EntitlementTypeId) ?? "",
          sellingSeasonDescription:
            headerValue?.sellingSeason?.seasonDescription ?? "",
          fulfillmentSeasonDescription:
            headerValue?.fulfillmentSeason?.seasonDescription ?? "",
          locationName:
            locationNames.get(
              `${context.LiftAccessRuleScopeCategory}:${context.LiftAccessRuleScopeEntityId}`,
            ) ?? "",
        },
      ]),
    );
  };

  /**
   * Shared by the first attempt and Retry, so both report through the same progress
   * card and can both land on the outcome screen.
   */
  const executeBulkAdd = async (
    run: BulkAddRun,
    request: AccessRuleMultiAddRequest,
  ) => {
    dispatch({
      type: ACTION_TYPE.BULK_ADD_LIFT_ACCESS_RULES_BATCH_RESET,
    });

    setShowAddFailures(false);
    setIsProgressVisible(true);

    // A fresh ref per attempt: a flag left set by the last cancel would stop this one
    // before its first request.
    cancelRef.current = { cancelled: false };
    setIsCancelling(false);

    let result: LiftAccessRuleBatchSaveResult | undefined;
    try {
      result = await dispatch(
        bulkAddLiftAccessRulesBatched(request, {
          cancelRef: cancelRef.current,
          concurrency: 2, // TODO:
        }),
      );
    } catch (e) {
      // The runner settles the card itself; if it dies before doing so, the card is
      // left with no Close button over a full-screen overlay.
      console.error("Bulk add run threw", e);
      dispatch({
        type: ACTION_TYPE.BULK_ADD_LIFT_ACCESS_RULES_BATCH_COMPLETE,
        payload: {},
      });
    }

    // Merged, not replaced: a retry only reports on what it re-sent, so replacing
    // would make everything added on the first attempt look unattempted.
    const outcomes = mergeSaveOutcomes(run.outcomes, result);
    const settled: BulkAddRun = {
      ...run,
      outcomes,
      cancelled: !!result?.cancelled,
    };
    setAddRun(settled);

    // Anything not confirmed added gets the outcome screen. Unattempted rule sets
    // count too: not failures, but nothing else on screen would report them.
    if (
      addFailureCount(settled) > 0 ||
      notAttemptedAddContexts(settled).length > 0
    ) {
      setShowAddFailures(true);
      return;
    }

    // A clean run stays on the progress card, which reports the success itself.
    showSnackbar(getResource(resources, "ACCESS_RULE_ADD_CREATED_RULES_SAVED"));
  };

  const handleCreate = async () => {
    const request = buildRequest();

    if (!request) {
      showSnackbar(
        getResource(resources, "ACCESS_RULE_ADD_VALIDATION_UNEXPECTED_ERROR"),
      );
      return;
    }

    setShowConfirmation(false);

    // One per rule set the request covers, not per request: what the outcome screen
    // and Retry both key on.
    const contexts = flattenAddContexts(request.Contexts);
    addRequestRef.current = request;

    await executeBulkAdd(
      {
        contexts,
        identities: buildAddIdentities(contexts),
        outcomes: new Map(),
        cancelled: false,
      },
      request,
    );
  };

  // Deterministic errors are left out — they would fail identically.
  const retryFailedAdds = () => {
    const request = addRequestRef.current;
    if (!addRun || !request) return;

    const contexts = retryableAddContexts(addRun);
    if (contexts.length === 0) return;

    void executeBulkAdd(addRun, {
      ...request,
      Contexts: groupContextsForAdd(contexts),
    });
  };

  const mapHeaderToOverviewQuery = (): AccessOverviewQuery => {
    const headerValue = editableHeaderRef.current?.getValue();

    if (!headerValue) {
      return createEmptyAccessOverviewQuery();
    }

    return {
      sellingSeasonIds: headerValue.sellingSeason
        ? [headerValue.sellingSeason.sellingSeasonId]
        : [],

      fulfillmentSeasonIds: headerValue.fulfillmentSeason
        ? [headerValue.fulfillmentSeason.fulfillmentSeasonId]
        : [],

      resortIds:
        headerValue.accessNodes
          ?.filter(
            (node) =>
              mapNodeTypeToScopeCategory(node.type) ===
              LIFT_ACCESS_RULE_SCOPE_CATEGORY.RESORT,
          )
          .map((node) => extractId(node.id))
          ?.filter((id): id is number => id != null && !isNaN(id)) || [],

      entitlementTypeIds: headerValue.entitlementTypeIds
        ? extractIds(headerValue.entitlementTypeIds as any)
        : [],

      entitlementDescriptionFilter: "",
    };
  };

  const handleCloseBulkProgressCard = () => {
    dispatch({
      type: ACTION_TYPE.BULK_ADD_LIFT_ACCESS_RULES_BATCH_RESET,
    });

    const overviewFilterQuery = mapHeaderToOverviewQuery();
    setIsProgressVisible(false);
    setShowAddFailures(false);
    setAddRun(null);
    addRequestRef.current = null;
    onCreate?.(overviewFilterQuery);
    onDismiss();
  };

  return (
    <>
      {/* Yields to the outcome screen, which reports the same run in more detail. */}
      <BulkProgressCard
        resources={resources}
        visible={isBusy && isProgressVisible && !showAddFailures}
        current={saveProgress?.current ?? 0}
        total={saveProgress?.total ?? 1}
        successCount={saveProgress?.successCount ?? 0}
        failureCount={saveProgress?.failureCount ?? 0}
        title={getResource(resources, "ACCESS_RULE_ADD_ADDING_ACCESS_RULES")}
        succeededTitle={getResource(resources, "ACCESS_RULE_SUCCEEDED")}
        failedTitle={getResource(resources, "ACCESS_RULE_FAILED")}
        itemTitle={getResource(resources, "ACCESS_RULE_ENTITLEMENT_RULE_SETS")}
        errorMessage={getResource(resources, "ACCESS_RULE_ADD_SAVE_ERROR")}
        showCancel
        // From the slice, not "processed everything": a run that stops early never
        // reaches its total and would otherwise leave no way to close the card.
        isComplete={saveProgress?.isComplete}
        isCancelled={saveProgress?.isCancelled}
        isCancelling={isCancelling}
        onClose={() => {
          handleCloseBulkProgressCard();
        }}
        onCancel={() => {
          cancelRef.current.cancelled = true;
          setIsCancelling(true);
        }}
      />

      <LiftAccessRuleSetEditorLayout
        header={
          <LiftAccessRuleSetEditableHeader
            ref={editableHeaderRef}
            resources={resources}
            sellingSeasons={sellingSeasons}
            fulfillmentSeasons={fulfillmentSeasons}
            entitlementTypes={entitlementTypes}
            accessNodes={accessNodes}
            onSellingSeasonChange={setSelectedSellingSeason}
          />
        }
        rules={
          <LiftAccessRulesEditor
            rules={editableRules}
            sellingSeason={selectedSellingSeason?.seasonDescription ?? ""}
            resources={resources}
            dateFormat={dateFormat}
            submitCount={submitCount}
            hasChangesSinceSubmit={hasChangesSinceSubmit}
            onAnyEdit={onAnyEdit}
            onRulesChange={setEditableRules}
          />
        }
      />

      <ErrorView error={validationError} />

      <Row
        justifyContent="flex-end"
        rootCss={css`
          ${ROW_SPACING};
          margin-top: 10px;
        `}
      >
        <Buttons.BigSecondary
          onClick={() => {
            resetChanges();
            onDismiss();
          }}
        >
          {getResource(resources, "ACCESS_RULE_MODAL_CANCEL")}
        </Buttons.BigSecondary>

        <Buttons.BigNeutral onClick={validateAndConfirm}>
          {getResource(resources, "ACCESS_RULE_MODAL_SAVE")}
        </Buttons.BigNeutral>
      </Row>

      {showConfirmation && (
        <ConfirmationModal
          resources={resources}
          icon={saveIcon}
          style={BoxedErrorInverse}
          title={getResource(resources, "ACCESS_RULE_MODAL_SAVE")}
          styledMessage={getResource(
            resources,
            "ACCESS_RULE_DATA_CHANGE_WARNING",
          )}
          message={getResource(resources, "ACCESS_RULE_ADD_SAVE_CONFIRMATION")}
          positiveButtonText={getResource(resources, "ACCESS_RULE_MODAL_SAVE")}
          negativeButtonText={getResource(
            resources,
            "ACCESS_RULE_MODAL_CANCEL",
          )}
          onPositiveButtonClick={handleCreate}
          onNegativeButtonClick={() => setShowConfirmation(false)}
          onDismiss={() => setShowConfirmation(false)}
        />
      )}

      {/* Which rule sets couldn’t be added, and why — a count alone doesn’t say which. */}
      {showAddFailures && addRun && (
        <BulkSaveFailuresView
          operation="add"
          failures={toAddFailures(addRun)}
          successCount={addSuccessCount(addRun)}
          failureCount={addFailureCount(addRun)}
          notAttemptedCount={notAttemptedAddContexts(addRun).length}
          // Wider than the listed failures: the unattempted go too, since re-sending
          // an add is safe.
          retryableCount={retryableAddContexts(addRun).length}
          cancelled={addRun.cancelled}
          onRetry={retryFailedAdds}
          onClose={handleCloseBulkProgressCard}
        />
      )}
    </>
  );
}
